import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";

/**
 * Car Company Service
 * Araç firma sayfaları içerik servisleri
 */
export const carCompanyService = {

  /**
   * Havalimani bilgilerini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   * }
   */
  getAllCompanies: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getAllCompanies,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Firma bilgilerini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   *   "Slug": "istanbul"
   * }
   */
  getCompanyInfo: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getCompanyInfo,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler kiralık araç listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   *   "CurrencyCode": "TRY"
   * }
   */

  getPopularCars: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getPopularCars,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Tüm ayrıcalıkları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr"
   * }
   */
  getPrivileges: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getPrivileges,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Tüm kampanyaları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   * }
   */
  getCampaigns: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getCampaigns,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler kiralama noktaları listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   * }
   */
  getRentalLocations: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getRentalLocations,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Kiralama şartları listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   * }
   */
  getNeedToKnow: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getNeedToKnow,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler tatil bölgelerini getirir
   * @param {Object} payload - API payload
   * @param {string} [payload.LanguageCode] - Dil kodu (örn: "tr", "en")
   * @param {string} [payload.CurrencyCode] - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Özel hata yönetimi kullanılsın mı
   * @returns {Promise<any>} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "CurrencyCode": "TRY"
   * }
   */
  getPopularLocations: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getLocations,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler firma listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   * }
   */
  getPopularCompanies: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getCompanies,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * SEO Text içeriğini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr"
   * }
   */
  getSEOText: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getSEOText,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Sık sorulan soruları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "CurrencyCode": "TRY"
   * }
   */
  getSss: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.company.getSss,
      payload,
      headers,
      hasErrorHandler,
    }),
};

